import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Gift } from "lucide-react";
import { toast } from "sonner";
import { startGiftCardCheckout, extractApiError } from "@/lib/api";

const presets = [50, 100, 250, 500];

export default function GiftCards() {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const effectiveAmount = custom ? Number(custom) : amount;

  const submit = async (e) => {
    e.preventDefault();
    if (!effectiveAmount || effectiveAmount < 25) {
      toast.error("Minimum $25.");
      return;
    }
    if (effectiveAmount > 2500) {
      toast.error("Maximum $2,500.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await startGiftCardCheckout({
        amount: effectiveAmount,
        buyer_name: buyerName,
        buyer_email: buyerEmail,
        recipient_name: recipientName,
        personal_note: note,
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(extractApiError(err, "Could not start checkout"));
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="gift-cards-page">
      <section className="pt-40 md:pt-52 pb-16 md:pb-24 mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Gift Cards</div>
        <h1 className="mt-4 font-display font-light tracking-tighter text-5xl md:text-8xl leading-none max-w-[16ch]">
          Give the night.
        </h1>
        <p className="mt-8 font-body text-lg md:text-2xl max-w-3xl leading-relaxed" style={{ color: "var(--ss-cream-dim)" }}>
          A Simply Social gift card is a signature cocktail hour, a DJ set for a rooftop, a private nightclub — waiting to happen. Perfect for weddings, showers, birthdays, and the holidays.
        </p>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-24 md:pb-32 grid lg:grid-cols-12 gap-10">
        {/* Preview card */}
        <div className="lg:col-span-5">
          <div className="relative overflow-hidden aspect-[3/4] border on-dark" style={{ borderColor: "var(--ss-border)", background: "linear-gradient(135deg, #1a1a1c 0%, #0F0E0C 100%)" }}>
            <div className="absolute inset-0 opacity-40 grain" />
            <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-10 z-10">
              <div>
                <div className="font-label text-xs mb-2" style={{ color: "var(--ss-gold)" }}>Simply Social</div>
                <div className="font-display text-4xl md:text-5xl leading-none">Gift Card</div>
              </div>
              <div>
                <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>Value</div>
                <div className="font-display text-6xl md:text-7xl" style={{ color: "var(--ss-rust)" }}>
                  ${effectiveAmount || 0}
                </div>
                {recipientName && (
                  <div className="mt-6">
                    <div className="font-label text-[0.65rem]" style={{ color: "var(--ss-cream-dim)" }}>For</div>
                    <div className="font-display text-2xl">{recipientName}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <p className="mt-6 font-body text-sm" style={{ color: "var(--ss-cream-dim)" }}>
            After payment, a unique gift code lands in your inbox. Forward or print it — recipients redeem by contacting us. No expiration.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-7 space-y-8" data-testid="gift-card-form">
          <div>
            <div className="font-label text-xs mb-4" style={{ color: "var(--ss-rust)" }}>Choose an amount</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {presets.map(p => {
                const active = !custom && amount === p;
                return (
                  <button key={p} type="button" data-testid={`gift-preset-${p}`}
                          onClick={() => { setAmount(p); setCustom(""); }}
                          className="font-label text-xs py-4 border transition-colors"
                          style={{
                            borderColor: active ? "var(--ss-rust)" : "var(--ss-border)",
                            background: active ? "var(--ss-rust)" : "transparent",
                            color: active ? "var(--ss-cream)" : "var(--ss-cream)",
                          }}>
                    ${p}
                  </button>
                );
              })}
            </div>
            <div className="mt-5">
              <label className="font-label text-xs" style={{ color: "var(--ss-cream-dim)" }}>Custom amount ($25 – $2,500)</label>
              <input type="number" min="25" max="2500" data-testid="gift-custom-amount" className="editorial-input mt-1"
                     placeholder="e.g. 175" value={custom} onChange={(e) => setCustom(e.target.value)} />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-8">
            <input required data-testid="gift-buyer-name" className="editorial-input" placeholder="Your Name"
                   value={buyerName} onChange={(e) => setBuyerName(e.target.value)} />
            <input required type="email" data-testid="gift-buyer-email" className="editorial-input" placeholder="Your Email"
                   value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} />
          </div>

          <input data-testid="gift-recipient-name" className="editorial-input" placeholder="Recipient's Name (optional)"
                 value={recipientName} onChange={(e) => setRecipientName(e.target.value)} />

          <div>
            <label className="font-label text-xs" style={{ color: "var(--ss-rust)" }}>Personal note (optional)</label>
            <textarea rows={4} data-testid="gift-note" className="editorial-input"
                      placeholder="Cheers to the two of you…"
                      value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
            <div>
              <div className="font-label text-xs" style={{ color: "var(--ss-cream-dim)" }}>Total</div>
              <div className="font-display text-4xl">${effectiveAmount || 0}</div>
            </div>
            <button type="submit" disabled={submitting} data-testid="gift-submit" className="btn-primary">
              <Gift size={14} /> {submitting ? "Redirecting…" : "Buy Gift Card"} <ArrowUpRight className="arrow" size={14} />
            </button>
          </div>
        </form>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-32">
        <div className="p-10 md:p-16 border flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
             style={{ borderColor: "var(--ss-border)", background: "var(--ss-bg-2)" }}>
          <div>
            <div className="font-label text-xs mb-2" style={{ color: "var(--ss-gold)" }}>Not sure yet?</div>
            <h3 className="font-display text-3xl md:text-5xl tracking-tight">Book a real event instead.</h3>
          </div>
          <Link to="/book" data-testid="gift-book-btn" className="btn-outline">Book Your Event <ArrowUpRight size={14} /></Link>
        </div>
      </section>
    </div>
  );
}
